import clsx from "clsx";

import { Icon, Input } from "@web-app/components";

import { ValidationState } from "../hooks/useRouteValidation";

interface RouteValidationErrorsProps {
  assigneeName?: string;
  assigneeValidation: ValidationState;
  dateTimezoneValidation: ValidationState;
  hasEarlyDeliveries?: boolean;
  hasPotentialDelayedOrders?: boolean;
  invalidTimestampOrders?: string[];
  isOverwriteAssignee: boolean;
  pickupTimeValidation?: ValidationState;
  sequenceValidation: ValidationState;
  setIsOverwriteAssignee: (value: boolean) => void;
  showReorderWarning?: boolean;
  workspaceValidation: ValidationState;
}

export function RouteValidationErrors({
  assigneeValidation,
  sequenceValidation,
  workspaceValidation,
  isOverwriteAssignee,
  dateTimezoneValidation,
  hasEarlyDeliveries,
  setIsOverwriteAssignee,
  assigneeName,
  hasPotentialDelayedOrders,
  invalidTimestampOrders,
  pickupTimeValidation,
  showReorderWarning,
}: RouteValidationErrorsProps) {
  const hasErrors =
    (!assigneeValidation.isValid && !isOverwriteAssignee) ||
    !sequenceValidation.isValid ||
    !workspaceValidation.isValid ||
    !dateTimezoneValidation.isValid ||
    (invalidTimestampOrders && invalidTimestampOrders.length > 0) ||
    (pickupTimeValidation && !pickupTimeValidation.isValid);

  return (
    <div className="flex flex-col gap-2">
      {!assigneeValidation.isValid && (
        <div
          className={clsx(
            "text-xs lg:text-sm p-2 rounded-md border last:mb-4",
            isOverwriteAssignee
              ? "bg-yellow-50 border-yellow-200 text-yellow-600"
              : "bg-red-50 border-red-200 text-red-600",
          )}
        >
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>{assigneeValidation.message}</span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Input
              type="checkbox"
              name=""
              value={isOverwriteAssignee ? "true" : "false"}
              onChange={(value) => setIsOverwriteAssignee(value === "true")}
              rightLabel={
                "Automatically assign all orders to " +
                (assigneeName || "the selected assignee")
              }
              labelClassName={clsx(
                isOverwriteAssignee ? "!text-yellow-600" : "!text-red-600",
              )}
            />
          </div>
        </div>
      )}
      {!sequenceValidation.isValid && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-red-50 border-red-200 text-red-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>{sequenceValidation.message}</span>
          </div>
        </div>
      )}
      {!workspaceValidation.isValid && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-red-50 border-red-200 text-red-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>{workspaceValidation.message}</span>
          </div>
        </div>
      )}
      {!dateTimezoneValidation.isValid && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-red-50 border-red-200 text-red-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>{dateTimezoneValidation.message}</span>
          </div>
        </div>
      )}
      {hasPotentialDelayedOrders && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-yellow-50 border-yellow-200 text-yellow-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>Some orders will be delayed</span>
          </div>
        </div>
      )}
      {hasEarlyDeliveries && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-blue-50 border-blue-200 text-blue-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>Some orders will be delivered earlier</span>
          </div>
        </div>
      )}
      {invalidTimestampOrders && invalidTimestampOrders.length > 0 && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-red-50 border-red-200 text-red-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>Routes from back dates cannot be created.</span>
          </div>
        </div>
      )}
      {pickupTimeValidation && !pickupTimeValidation.isValid && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-red-50 border-red-200 text-red-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>{pickupTimeValidation.message}</span>
          </div>
        </div>
      )}
      {showReorderWarning && !hasErrors && (
        <div className="text-xs lg:text-sm p-2 rounded-md border last:mb-4 bg-yellow-50 border-yellow-200 text-yellow-600">
          <div className="flex items-center gap-2">
            <Icon name="fasExclamationTriangle" className="h-5 w-5" />
            <span>
              Orders rearranged. Please click "Compute" or "Optimize" to get new
              ETAs
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
